import styled from "styled-components";
import { useState, useMemo } from "react";
// import InfiniteScroll from "react-infinite-scroll-component";

// import { Reservation } from "models/reservations";
import { PropertyReportDataItem } from "components/molecules";
import { ListLoader, LoaderWithOverlay, Icon } from "components/atoms";
import { ColumnSettingsModal } from "components/organisms";
import { useColumnPreferences } from "hooks/useColumnPreferences";

import { FONT_FAMILY_MEDIUM } from "styles/typography";
import { MAIN_THEME } from "styles/colors";

import { ReportTableItemWithTransfer } from "interfaces/reports";
import { IconName } from "interfaces/icons";

// interface TableProps {
// 	columns: string;
// }

const ReportItemsTable = styled.div`
	margin-bottom: 25px;
	padding: 15px 30px 40px;
	overflow-y: auto;
	
	@media (max-width: 768px) {
		padding: 15px 10px 40px;
	}
`;

const ScrollWrapper = styled.div`
	width: 100%;
	overflow-x: auto;
	overflow-y: visible;
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 12px;
	background: transparent;

	/* Custom scrollbar styling */
	&::-webkit-scrollbar {
		height: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}
	
	&::-webkit-scrollbar-thumb {
		background: ${MAIN_THEME.DARK_BG_TERTIARY};
		border-radius: 4px;
		
		&:hover {
			background: ${MAIN_THEME.GREY};
		}
	}
`;

const TableContent = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: 0;
	overflow: hidden;
	background: transparent;
`;

const TableContainer = styled.div<{ mobileMinWidth: string }>`
	min-width: 1400px;
	width: 100%;
	display: inline-flex;
	flex-direction: column;
	overflow: hidden;
	background: transparent;
	position: relative;

	@media (max-width: 768px) {
		min-width: ${(props) => props.mobileMinWidth};
	}
`;


const TableHeaderItem = styled.div`
	font-size: 14px;
	text-align: left;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	height: 52px;
	padding: 10px 8px;
	color: ${MAIN_THEME.TEXT_SECONDARY};
	background: transparent;
	font-family: ${FONT_FAMILY_MEDIUM};
	font-weight: 500;
	text-transform: none;
	letter-spacing: 0.3px;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};


	@media (max-width: 768px) {
		font-size: 12px;
		height: 36px;
	}
`;

const EmptyListMessageContainer = styled.div`
	background: transparent;
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	margin: 20px 0;
	padding: 40px;
	border-radius: 12px;
`;

const EmptyListMessage = styled.p`
	font-family: ${FONT_FAMILY_MEDIUM};
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 16px;
	text-align: center;
	margin: 0;
`;

const ReportTableHeader = styled.div<{ columns: string }>`
	display: grid;
	grid-template-columns: ${(props) => props.columns};
	gap: 0;
	margin-bottom: 0;
	overflow: hidden;
	background: transparent;
`;

const LoaderContainer = styled.div`
	margin-bottom: 25px;
`;

const SettingsButton = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 10px;
	margin-left: auto;

	&:hover {
		opacity: 0.8;
	}
`;

const SummaryItemHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;
	padding: 0 20px;
`;

const ContentHeader = styled.div`
	display: flex;
	justify-content: flex-end;
`;

const DEFAULT_COLUMNS = [
	{ id: 'property', label: 'Property', isVisible: true, width: '1fr' },
	{ id: 'channel', label: 'Channel', isVisible: true, width: '1fr' },
	{ id: 'code', label: 'Code', isVisible: true, width: '1fr' },
	{ id: 'status', label: 'Status', isVisible: true, width: '1fr' },
	{ id: 'fullName', label: 'Full name', isVisible: true, width: '0.75fr' },
	{ id: 'checkin', label: 'Checkin', isVisible: true, width: '0.75fr' },
	{ id: 'checkout', label: 'Checkout', isVisible: true, width: '0.65fr' },
	{ id: 'currency', label: 'Currency', isVisible: true, width: '1fr' },
	{ id: 'accommodation', label: 'Accommodation', isVisible: true, width: '1fr' },
	{ id: 'channelFee', label: 'Channel Fee', isVisible: true, width: '1fr' },
	{ id: 'netAccommodation', label: 'Net accomdation', isVisible: true, width: '1fr' },
	{ id: 'cleaning', label: 'Cleaning', isVisible: true, width: '1fr' },
	{ id: 'payout', label: 'Payout', isVisible: true, width: '1fr' },
	{ id: 'hoteliseHelp', label: 'Hotelise help', isVisible: true, width: '1fr' },
	{ id: 'ownerIncome', label: 'Owner income', isVisible: true, width: '1fr' },
	{ id: 'transfer', label: 'Transfer', isVisible: true, width: '1fr' },
	{ id: 'sender', label: 'Sender', isVisible: true, width: '0.5fr' },
];

interface Props {
	isLoading: boolean;
	showProperty?: boolean;
	reservations: any[];
	onSelect: (item: ReportTableItemWithTransfer | null) => void;
}

const ReservationsList = (props: Props): JSX.Element => {
	const { showProperty, reservations, isLoading, onSelect } = props;
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

	const { columns, toggleColumn } = useColumnPreferences("reservationsList", DEFAULT_COLUMNS);

	const visibleColumns = useMemo(() => {
		return columns.filter(col => {
			if (!showProperty && col.id === 'property') return false;
			return col.isVisible;
		});
	}, [columns, showProperty]);

	const visibleColumnIds = useMemo(() => visibleColumns.map(c => c.id), [visibleColumns]);

	const gridTemplateColumns = useMemo(() => {
		return visibleColumns.map(c => c.width).join(" ");
	}, [visibleColumns]);

	const mobileMinWidth = useMemo(() => {
		return `${Math.max(visibleColumns.length * 120, 100)}px`;
	}, [visibleColumns]);

	const isColumnVisible = (id: string) => visibleColumnIds.includes(id);

	const renderReportItemsTable = () => {
		const hasReservations = reservations.length > 0;
		return (
			<ReportItemsTable>
				<SummaryItemHeader>
					<div style={{ visibility: 'hidden' }}>spacer</div>
					<SettingsButton onClick={() => setIsSettingsModalOpen(true)} style={{ margin: 0 }}>
						<Icon name={IconName.SETTINGS} color={MAIN_THEME.TEXT_PRIMARY} size={20} />
					</SettingsButton>
				</SummaryItemHeader>
				<ScrollWrapper>
					<TableContainer mobileMinWidth={mobileMinWidth}>
						{isLoading ? <LoaderWithOverlay /> : null}
						{hasReservations ? (
							<>
								<ReportTableHeader
									columns={gridTemplateColumns}
								>
									{isColumnVisible('property') ? (
										<TableHeaderItem>Property</TableHeaderItem>
									) : null}
									{isColumnVisible('channel') && <TableHeaderItem>Channel</TableHeaderItem>}
									{isColumnVisible('code') && <TableHeaderItem>Code</TableHeaderItem>}
									{isColumnVisible('status') && <TableHeaderItem>Status</TableHeaderItem>}
									{isColumnVisible('fullName') && <TableHeaderItem>Full name</TableHeaderItem>}
									{isColumnVisible('checkin') && <TableHeaderItem>Checkin</TableHeaderItem>}
									{isColumnVisible('checkout') && <TableHeaderItem>Checkout</TableHeaderItem>}
									{isColumnVisible('currency') && <TableHeaderItem>Currency</TableHeaderItem>}
									{isColumnVisible('accommodation') && <TableHeaderItem>Accommodation</TableHeaderItem>}
									{isColumnVisible('channelFee') && <TableHeaderItem>Channel Fee</TableHeaderItem>}
									{isColumnVisible('netAccommodation') && <TableHeaderItem>Net accomdation</TableHeaderItem>}
									{isColumnVisible('cleaning') && <TableHeaderItem>Cleaning</TableHeaderItem>}
									{isColumnVisible('payout') && <TableHeaderItem>Payout</TableHeaderItem>}
									{isColumnVisible('hoteliseHelp') && <TableHeaderItem>Hotelise help</TableHeaderItem>}
									{isColumnVisible('ownerIncome') && <TableHeaderItem>Owner income</TableHeaderItem>}
									{isColumnVisible('transfer') && <TableHeaderItem>Transfer</TableHeaderItem>}
									{isColumnVisible('sender') && <TableHeaderItem>Sender</TableHeaderItem>}
								</ReportTableHeader>
								<TableContent>
									{reservations.map(
										(reservation: ReportTableItemWithTransfer) => (
											<PropertyReportDataItem
												key={reservation._item.code}
												data={reservation}
												showProperty={showProperty}
												onSelect={onSelect}
												visibleColumnIds={visibleColumnIds}
												gridTemplateColumns={gridTemplateColumns}
											/>
										)
									)}
								</TableContent>
							</>
						) : (
							<EmptyListMessageContainer>
								<EmptyListMessage>Empty list</EmptyListMessage>
							</EmptyListMessageContainer>
						)}
					</TableContainer>
				</ScrollWrapper>
				<ColumnSettingsModal
					isVisible={isSettingsModalOpen}
					close={() => setIsSettingsModalOpen(false)}
					columns={columns.filter(c => showProperty || c.id !== 'property')}
					onToggle={toggleColumn}
				/>
			</ReportItemsTable>
		);
	};

	return renderReportItemsTable();
};

export default ReservationsList;
